import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavigationBar from '../../components/ui/NavigationBar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FileMetadataPanel from './components/FileMetadataPanel';
import ImagePreview from './components/ImagePreview';
import DocumentPreview from './components/DocumentPreview';
import VideoPreview from './components/VideoPreview';
import GenericFilePreview from './components/GenericFilePreview';
import DeleteConfirmationDialog from './components/DeleteConfirmationDialog';

const FilePreview = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentFile, setCurrentFile] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMetadataExpanded, setIsMetadataExpanded] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const mockFiles = [
  {
    id: 1,
    name: "product-hero-banner.jpg",
    type: "image/jpeg",
    size: 2458624,
    lastModified: new Date("2025-12-15T10:30:00"),
    url: "https://images.unsplash.com/photo-1735405072033-7ea120e1e265",
    alt: "Modern minimalist product photography showing sleek electronic device on white marble surface with soft natural lighting",
    path: "s3://production-assets/images/banners/product-hero-banner.jpg",
    s3Properties: {
      storageClass: "STANDARD",
      etag: "d41d8cd98f00b204e9800998ecf8427e",
      encryption: "AES256",
      acl: "private"
    }
  },
  {
    id: 2,
    name: "quarterly-report-q4-2025.pdf",
    type: "application/pdf",
    size: 1548288,
    lastModified: new Date("2025-12-18T14:20:00"),
    url: "/assets/documents/quarterly-report.pdf",
    alt: "PDF document icon representing quarterly financial report",
    path: "s3://production-assets/documents/reports/quarterly-report-q4-2025.pdf",
    s3Properties: {
      storageClass: "STANDARD_IA",
      etag: "a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6",
      encryption: "AES256",
      acl: "private"
    }
  },
  {
    id: 3,
    name: "team-meeting-recording.mp4",
    type: "video/mp4",
    size: 45678912,
    lastModified: new Date("2025-12-19T09:15:00"),
    url: "/assets/videos/team-meeting.mp4",
    alt: "Video file icon representing team meeting recording",
    duration: "01:23:45",
    path: "s3://production-assets/videos/meetings/team-meeting-recording.mp4",
    s3Properties: {
      storageClass: "GLACIER",
      etag: "z9y8x7w6v5u4t3s2r1q0p9o8n7m6l5k4",
      encryption: "AES256",
      acl: "private"
    }
  },
  {
    id: 4,
    name: "customer-testimonial-photo.jpg",
    type: "image/jpeg",
    size: 1876543,
    lastModified: new Date("2025-12-17T16:45:00"),
    url: "https://img.rocket.new/generatedImages/rocket_gen_img_1ffde516e-1763293580273.png",
    alt: "Professional portrait of smiling businesswoman with long brown hair wearing navy blazer in modern office setting",
    path: "s3://production-assets/images/testimonials/customer-testimonial-photo.jpg",
    s3Properties: {
      storageClass: "STANDARD",
      etag: "f1e2d3c4b5a6978685746352413021",
      encryption: "AES256",
      acl: "public-read"
    }
  },
  {
    id: 5,
    name: "data-backup-archive.zip",
    type: "application/zip",
    size: 125829120,
    lastModified: new Date("2025-12-10T22:00:00"),
    url: "/assets/archives/data-backup.zip",
    alt: "Archive file icon representing compressed data backup",
    path: "s3://production-assets/backups/data-backup-archive.zip",
    s3Properties: {
      storageClass: "DEEP_ARCHIVE",
      etag: "1a2b3c4d5e6f7g8h9i0j1k2l3m4n5o6p",
      encryption: "AES256",
      acl: "private"
    }
  }];


  useEffect(() => {
    const fileId = location?.state?.fileId;
    if (fileId) {
      const fileIndex = mockFiles?.findIndex((f) => f?.id === fileId);
      if (fileIndex !== -1) {
        setCurrentIndex(fileIndex);
        setCurrentFile(mockFiles?.[fileIndex]);
      } else {
        setCurrentFile(mockFiles?.[0]);
      }
    } else {
      setCurrentFile(mockFiles?.[0]);
    }
  }, [location?.state]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        handleClose();
      } else if (e?.key === 'ArrowLeft') {
        handlePrevious();
      } else if (e?.key === 'ArrowRight') {
        handleNext();
      } else if (e?.key === 'd' && (e?.ctrlKey || e?.metaKey)) {
        e?.preventDefault();
        handleDownload();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex]);

  const handleClose = () => {
    navigate('/file-browser');
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
      setCurrentFile(mockFiles?.[newIndex]);
    }
  };

  const handleNext = () => {
    if (currentIndex < mockFiles?.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
      setCurrentFile(mockFiles?.[newIndex]);
    }
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    console.log('Downloading file:', currentFile?.name);
    setIsDownloading(false);
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log('Deleting file:', currentFile?.name);
    setIsDeleting(false);
    setIsDeleteDialogOpen(false);
    navigate('/file-browser');
  };

  const handleShare = () => {
    console.log('Sharing file:', currentFile?.name);
  };

  const renderFilePreview = () => {
    if (!currentFile) return null;

    if (currentFile?.type?.startsWith('image/')) {
      return <ImagePreview file={currentFile} />;
    } else if (currentFile?.type === 'application/pdf' || currentFile?.type?.includes('document') ||
    currentFile?.type?.includes('word') ||
    currentFile?.type?.includes('sheet') ||
    currentFile?.type?.includes('presentation')) {
      return <DocumentPreview file={currentFile} />;
    } else if (currentFile?.type?.startsWith('video/')) {
      return <VideoPreview file={currentFile} />;
    } else {
      return <GenericFilePreview file={currentFile} />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes?.[i];
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!currentFile) {
    return (
      <>
        <NavigationBar />
        <div className="main-content flex items-center justify-center">
          <div className="text-center">
            <Icon name="FileQuestion" size={64} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No file selected</p>
          </div>
        </div>
      </>);

  }

  return (
    <>
      <NavigationBar />
      <div className="main-content bg-background min-h-screen">
        <div className="fixed inset-0 top-16 bg-background z-40">
          <div className="h-full flex flex-col">
            <div className="bg-card border-b border-border px-4 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleClose}
                  className="flex-shrink-0">

                  <Icon name="X" size={20} />
                </Button>
                <div className="flex-1 min-w-0">
                  <h1 className="text-base font-semibold text-foreground truncate">
                    {currentFile?.name}
                  </h1>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span className="font-mono">{formatFileSize(currentFile?.size)}</span>
                    <span>•</span>
                    <span>{formatDate(currentFile?.lastModified)}</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="hidden sm:flex items-center gap-2 mr-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handlePrevious}
                    disabled={currentIndex === 0}>

                    <Icon name="ChevronLeft" size={20} />
                  </Button>
                  <span className="text-xs text-muted-foreground font-mono min-w-[60px] text-center">
                    {currentIndex + 1} / {mockFiles?.length}
                  </span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleNext}
                    disabled={currentIndex === mockFiles?.length - 1}>

                    <Icon name="ChevronRight" size={20} />
                  </Button>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  iconName="Share2"
                  iconPosition="left"
                  onClick={handleShare}
                  className="hidden md:flex">

                  Share
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={handleDeleteClick}
                  className="hidden md:flex">

                  Delete
                </Button>
                <Button
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  onClick={handleDownload}
                  loading={isDownloading}>

                  Download
                </Button>
              </div>
            </div>

            <div className="flex-1 overflow-hidden">
              <div className="h-full flex flex-col lg:flex-row gap-4 p-4">
                <div className="flex-1 min-h-0">
                  {renderFilePreview()}
                </div>

                <div className="lg:w-80 space-y-4 overflow-y-auto scrollbar-thin">
                  <FileMetadataPanel
                    file={currentFile}
                    isExpanded={isMetadataExpanded}
                    onToggle={() => setIsMetadataExpanded(!isMetadataExpanded)} />


                  <div className="md:hidden space-y-2">
                    <Button
                      variant="outline"
                      fullWidth
                      iconName="Share2"
                      iconPosition="left"
                      onClick={handleShare}>

                      Share File
                    </Button>
                    <Button
                      variant="destructive"
                      fullWidth
                      iconName="Trash2"
                      iconPosition="left"
                      onClick={handleDeleteClick}>

                      Delete File
                    </Button>
                  </div>

                  <div className="sm:hidden bg-card border border-border rounded-lg p-4">
                    <p className="text-xs text-muted-foreground mb-3">Navigation</p>
                    <div className="flex items-center justify-between">
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ChevronLeft"
                        iconPosition="left"
                        onClick={handlePrevious}
                        disabled={currentIndex === 0}>

                        Previous
                      </Button>
                      <span className="text-xs text-muted-foreground font-mono">
                        {currentIndex + 1} / {mockFiles?.length}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="ChevronRight"
                        iconPosition="right"
                        onClick={handleNext}
                        disabled={currentIndex === mockFiles?.length - 1}>

                        Next
                      </Button>
                    </div>
                  </div>

                  <div className="bg-muted rounded-lg p-4">
                    <h3 className="text-xs font-semibold text-foreground mb-3">
                      Keyboard Shortcuts
                    </h3>
                    <div className="space-y-2 text-xs">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Close preview</span>
                        <kbd className="px-2 py-1 bg-card border border-border rounded text-foreground font-mono">
                          Esc
                        </kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Previous file</span>
                        <kbd className="px-2 py-1 bg-card border border-border rounded text-foreground font-mono">
                          ←
                        </kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Next file</span>
                        <kbd className="px-2 py-1 bg-card border border-border rounded text-foreground font-mono">
                          →
                        </kbd>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Download</span>
                        <kbd className="px-2 py-1 bg-card border border-border rounded text-foreground font-mono">
                          Ctrl+D
                        </kbd>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <DeleteConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        file={currentFile}
        isDeleting={isDeleting} />

    </>);

};

export default FilePreview;